<style lang="scss" scoped>
@import "@/styles/components/shared-styles/IdMe.style.scss";
</style>
<template>
  <div id="app" ref="main-app" :class="{ 'nav-visible': this.$store.getters.getOffCanvasMenuStatus }">
    <div id="app-canvas" direction="vertical">
      <a-modal v-if="openModal" v-model="openModal" class="session-expired-modal" title="Session Timeout" ok-text="OK" :footer="null" :closable="false" @ok="() => null">
        <div class="modal-content">
          <div class="text-content">
            <font-awesome-icon class="icon" icon="exclamation-circle" />
            <p>Sorry!! Your session has timed out. Please log in again.</p>
          </div>
          <BaseButton type="primary" button-text="OK" :handle-click="() => reloadApp()" />
        </div>
      </a-modal>
      <a-modal v-if="showInactivityModal" v-model="showInactivityModal" class="session-about-to-expire-modal" title="Session Inactivity" :footer="null" :closable="false">
        <div class="modal-content">
          <div class="text-content">
            <font-awesome-icon class="icon" icon="exclamation-circle" />
            <p>You are about to be logged out due to inactivity. <br />Remain logged in?</p>
            <p class="countdown">You will be logged out in {{ logoutCountdown }} seconds.</p>
          </div>
          <div class="modal-footer">
            <BaseButton type="primary" button-text="Yes" :handle-click="keepSessionActive" />
            <BaseButton type="" button-text="No" :handle-click="logoutUser" />
          </div>
        </div>
      </a-modal>
      <router-view />
      <LoadingScreen v-if="showLoading" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import BaseButton from "@/components/common/AntDesign/BaseButton.vue";
import LoadingScreen from "@/components/shared-components/LoadingScreen/LoadingScreen.vue";
import { findButtonImage } from "@/utils/imagePlaceholder";

export default {
  name: "App",
  components: {
    BaseButton,
    LoadingScreen,
  },
  data() {
    return {
      inactivityTimeout: null,
      showInactivityModal: false,
      logoutTimeout: null,
      logoutCountdown: 30,
      INACTIVITY_TIME: 3600000,
      COUNTDOWN_TIME: 30,
      debounceTimer: null,
      DEBOUNCE_DELAY: 1000,
      tabId: null,
      lastActivityTime: Date.now(),
      isDevMode: process.env.NODE_ENV === "development",
    };
  },
  computed: {
    ...mapGetters("globalStore", ["getSessionExpired"]),
    ...mapGetters(["getSelectedCurrentLocationId"]),
    openModal: {
      get() {
        const exp = localStorage.getItem("exp");
        const accessToken = localStorage.getItem("access_token");
        return this.getSessionExpired && !["", null, undefined].includes(exp) && !["", null, undefined].includes(accessToken);
      },
      set(newVal) {
        return newVal;
      },
    },
    showLoading() {
      return !this.$route.name;
    },
  },
  async mounted() {
    // Skip inactivity tracking in development mode unless specifically testing
    const urlParams = new URLSearchParams(window.location.search);
    const testInactivity = urlParams.get("test_inactivity");

    if (this.isDevMode && !testInactivity) {
      console.log("Development mode: Inactivity tracking disabled");
      return;
    }

    // Generate a unique ID for this tab
    this.tabId = Math.random().toString(36).substring(2, 15);
    // Set initial activity timestamp in localStorage
    this.updateLastActivityTime();
    // Listen for activity from other tabs
    window.addEventListener("storage", this.handleStorageChange);
    // Check for test parameter in URL
    if (testInactivity === "true") {
      this.showInactivityModal = true;
      this.startLogoutTimer();
      return;
    }

    setTimeout(async () => {
      const { isAuthenticated } = await this.$auth;
      if (isAuthenticated) {
        this.setupActivityListeners();
        this.startInactivityTimer();
      }
    }, 10000);
  },
  beforeDestroy() {
    clearTimeout(this.inactivityTimeout);
    clearInterval(this.logoutTimeout);
    clearTimeout(this.debounceTimer);

    // Remove event listener
    window.removeEventListener("storage", this.handleStorageChange);
  },
  methods: {
    ...mapActions(["clearLocalStore"]),
    ...mapActions("globalStore", ["setSessionExpired"]),
    updateLastActivityTime() {
      // Update last activity time in localStorage
      const now = Date.now();
      this.lastActivityTime = now;
      try {
        // Store last activity time and tab ID in localStorage
        localStorage.setItem("app_last_activity", JSON.stringify({
          timestamp: now,
          tabId: this.tabId
        }));
      } catch (e) {
        console.error("Error updating activity time:", e);
      }
    },
    handleStorageChange(event) {
      // Handle storage change events from other tabs
      if (event.key === "app_last_activity") {
        try {
          const data = JSON.parse(event.newValue);
          // Only update if it's from another tab
          if (data && data.tabId !== this.tabId) {
            if (data.manualRefresh) {
              // Another tab clicked "Yes" - hide our modal too
              if (this.showInactivityModal) {
                this.showInactivityModal = false;
                clearInterval(this.logoutTimeout);
                this.startInactivityTimer();
              }
            }
            this.lastActivityTime = Math.max(this.lastActivityTime, data.timestamp);
          }
        } catch (e) {
          console.error("Error handling storage event:", e);
        }
      }
    },
    hasRecentActivity() {
      // Check if there's been activity in any tab
      const now = Date.now();
      // Check if time since last activity is less than inactivity time
      return (now - this.lastActivityTime) < this.INACTIVITY_TIME;
    },
    async reloadApp() {
      const { logout } = await this.$auth;
      await logout();
    },
    findButtonImage(c) {
      return findButtonImage(c);
    },
    setupActivityListeners() {
      // Skip in development mode unless testing
      if (this.isDevMode && !new URLSearchParams(window.location.search).get("test_inactivity")) {
        return;
      }

      const resetInactivityTimer = () => {
        if (this.showInactivityModal) {
          return; // Don't reset timer if modal is showing
        }
        // Clear existing debounce timer
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        // Set new debounce timer
        this.debounceTimer = setTimeout(() => {
          clearTimeout(this.inactivityTimeout);
          // Update activity timestamp across tabs
          this.updateLastActivityTime();
          this.startInactivityTimer();
        }, this.DEBOUNCE_DELAY);
      };

      // Add these event listeners to track user activity
      const events = ["mousemove", "keypress", "click", "touchstart", "scroll"];
      events.forEach(event => {
        window.addEventListener(event, resetInactivityTimer);
      });
    },

    startInactivityTimer() {
      // Skip in development mode unless testing
      if (this.isDevMode && !new URLSearchParams(window.location.search).get("test_inactivity")) {
        return;
      }

      clearTimeout(this.inactivityTimeout);
      this.inactivityTimeout = setTimeout(() => {
        // Check if there's been activity in any tab
        if (!this.hasRecentActivity()) {
          this.showInactivityModal = true;
          this.startLogoutTimer();
        } else {
          this.startInactivityTimer();
        }
      }, this.INACTIVITY_TIME);
    },

    startLogoutTimer() {
      this.logoutCountdown = this.COUNTDOWN_TIME;
      clearInterval(this.logoutTimeout);

      this.logoutTimeout = setInterval(() => {
        this.logoutCountdown--;
        if (this.logoutCountdown <= 0) {
          clearInterval(this.logoutTimeout);
          this.logoutUser();
        }
      }, 1000);
    },

    async keepSessionActive() {
      try {
        // Reset inactivity modal and timers
        this.showInactivityModal = false;
        clearInterval(this.logoutTimeout);
        clearTimeout(this.inactivityTimeout);

        // Refresh token using the same method as router-guard.js
        const { getAccessTokenSilently } = await this.$auth;
        await getAccessTokenSilently();
        // Update activity timestamp with a special flag that this was a manual refresh
        const now = Date.now();
        this.lastActivityTime = now;
        // Update localStorage with a special type to signal other tabs
        localStorage.setItem("app_last_activity", JSON.stringify({
          timestamp: now,
          tabId: this.tabId,
          manualRefresh: true // Signal that this was a manual refresh (Yes button)
        }));
        // Restart inactivity timer
        this.startInactivityTimer();
      } catch (error) {
        console.error("Error refreshing session:", error);
        this.$notification.error({
          message: "Session Refresh Failed",
          description: "There was an error refreshing your session. You may need to log in again soon."
        });
      }
    },
    async logoutUser() {
      clearInterval(this.logoutTimeout);
      clearTimeout(this.inactivityTimeout);
      this.showInactivityModal = false;
      // Clear session data
      await this.clearLocalStore();
      await this.setSessionExpired(true);
      // Logout user
      const { logout } = await this.$auth;
      await logout();
    },
  },
};
</script>

<style lang="scss" scoped>
.idme-widget-container {
  width: 200px;
  height: 200px;
}

#app {
  overflow: hidden;
  background-color: #f5f7fa;
}

#app-canvas {
  height: 100%;
  transform: translate3d(0, 0, 0);
  transition: ease all 300ms;
  backface-visibility: hidden;
}

.nav-visible {
  #app-canvas {
    transform: translate3d(300px, 0, 0);
  }
}

.session-expired-modal ::v-deep {
  .ant-modal-header {
    background: #ec5d57;
  }

  .ant-modal-title {
    color: white;
  }

  .modal-content .text-content {
    display: flex;
    align-items: center;
    font-size: 17px;
    margin-bottom: 15px;
  }

  .modal-content .text-content svg {
    margin-right: 10px;
    color: #ec5d57;
    font-size: 22px;
  }

  .modal-content .text-content p {
    margin: 0;
  }
}

@media (max-width: 767px) {
  .nav-visible {
    #app-canvas {
      transform: translate3d(300px, 0, 0);
    }
  }
}

.modal-content {
  .text-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    .icon {
      font-size: 24px;
      color: #ec5d57;
      margin-bottom: 12px;
    }

    p {
      margin: 8px 0;
      font-size: 16px;

      &.countdown {
        font-weight: bold;
        color: #ec5d57;
      }
    }
  }

  .modal-footer {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 20px;
  }
}

.session-about-to-expire-modal {
  ::v-deep {
    .ant-modal-body {
      display: flex;
      flex-direction: column;
    }
  }
}
</style>
